import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isFinite from 'lodash/isFinite';
import isEmpty from 'lodash/isEmpty';
import without from 'lodash/without';
import { prop } from 'ramda';
import Checkbox from '../../common/Checkbox';
import { AUDIENCE_TYPE } from '../constants';
import { fullName } from '../../common/helpers/users';
import Tooltip from '../../common/tooltip';
import { getByType } from '../selectors';
import { t } from '../../../modules/infra/i18n';

const audienceNameMap = {
  [AUDIENCE_TYPE.all]: () => t('engage:audience.all_customers'),
  [AUDIENCE_TYPE.regular]: () => t('engage:audience.regulars'),
  [AUDIENCE_TYPE.lapsing]: () => t('engage:audience.lapsed'),
  [AUDIENCE_TYPE.new]: () => t('engage:audience.new_clients'),
  [AUDIENCE_TYPE.service]: () => t('engage:audience.service'),
  [AUDIENCE_TYPE.prospective]: () => t('engage:audience.prospective_clients'),
};

const BARBER_SORT_PRIORITY = 5;

const audienceSortMap = {
  [AUDIENCE_TYPE.new]: 1,
  [AUDIENCE_TYPE.service]: 2,
  [AUDIENCE_TYPE.regular]: 3,
  [AUDIENCE_TYPE.lapsing]: 4,
  [AUDIENCE_TYPE.barber]: BARBER_SORT_PRIORITY,
};

const audienceDescriptionMap = {
  [AUDIENCE_TYPE.regular]: () => t('engage:audience.regulars_description'),
  [AUDIENCE_TYPE.lapsing]: () => t('engage:audience.lapsed_description'),
  [AUDIENCE_TYPE.prospective]: () => t('engage:audience.prospective_description'),
};

const getAudienceName = (audienceItem = {}) => getByType(
  fullName,
  prop('label'),
  item => audienceNameMap[item.name] ? audienceNameMap[item.name]() : item.name,
)(audienceItem);

const getAudienceCustomers = ({ customers } = {}) => {
  if (!isFinite(customers)) {
    return '';
  }
  return `(${customers})`;
};

const getAudienceText = (audienceItem = {}) => `${getAudienceName(audienceItem)} ${getAudienceCustomers(audienceItem)}`;

const isAllSelected = (selected = []) => selected.includes(AUDIENCE_TYPE.all);

const getAudienceSortPriority = audienceKey => audienceSortMap[audienceKey] || BARBER_SORT_PRIORITY;
const sortAudienceKeys = (a, b) => getAudienceSortPriority(a) - getAudienceSortPriority(b);
const getAudienceKeys = (audience = {}) => {
  const audienceKeys = Object.keys(audience).sort(sortAudienceKeys);
  return without(audienceKeys, AUDIENCE_TYPE.all);
};

const buttonClassName = isSelected =>
  classnames('c-btn c-btn--white c-btn--active-branding u-1/1', {
    'is-active': isSelected,
  });

const SelectAudience = ({
  audience,
  selected,
  selectAll,
  selectCustom,
  toggleAudience,
  newCustomerWeeks,
  changeNewCustomerWeeks,
  services,
  serviceCustomerGroupServiceId,
  changeServiceCustomerGroupServiceId,
}) => (
  <div>
    <h2 className="c-heading">{t('engage:audience.select_title')}</h2>
    <p className="epsilon u-type--light">{t('engage:audience.how_to_reach')}</p>
    <div className="o-flexbox u-mt u--ml">
      <div className="o-flexbox__item u-pl u-flex-1">
        <button
          type="button"
          className={buttonClassName(isAllSelected(selected))}
          onClick={selectAll}
          disabled={!audience[AUDIENCE_TYPE.all]}
        >
          {audience[AUDIENCE_TYPE.all] ? getAudienceText(audience[AUDIENCE_TYPE.all]) : t('engage:audience.all_customers') }
        </button>
      </div>
      <div className="o-flexbox__item u-flex-1 u-pl">
        <button type="button" className={buttonClassName(!isAllSelected(selected))} onClick={selectCustom}>
          {t('engage:audience.custom')}
        </button>
      </div>
    </div>
    <ul className="layout u-mt" display-if={!isAllSelected(selected) && !isEmpty(audience)}>
      {getAudienceKeys(audience).map(key => (
        <li className="layout__item" key={key}>
          <div className="layout layout--middle layout--small layout--auto">
            <div className="layout__item">
              <Checkbox checked={selected.includes(key)} onChange={toggleAudience(audience[key])}>
                <span className="delta">{getAudienceText(audience[key])}</span>
                <Tooltip display-if={audienceDescriptionMap[key]}>
                  {audienceDescriptionMap[key] ? audienceDescriptionMap[key]() : ''}
                </Tooltip>
              </Checkbox>
            </div>

            <div className="layout__item" display-if={key === AUDIENCE_TYPE.new}>
              <select
                className="c-select c-select--small"
                onChange={changeNewCustomerWeeks}
                value={newCustomerWeeks}
                {...{}}
              >
                <option value={1}>{t('engage:audience.week_options.1')}</option>
                <option value={2}>{t('engage:audience.week_options.2')}</option>
                <option value={3}>{t('engage:audience.week_options.3')}</option>
                <option value={4}>{t('engage:audience.week_options.4')}</option>
                <option value={5}>{t('engage:audience.week_options.5')}</option>
                <option value={6}>{t('engage:audience.week_options.6')}</option>
              </select>
            </div>

            <div className="layout__item" display-if={key === AUDIENCE_TYPE.service}>
              <select
                className="c-select c-select--small"
                onChange={changeServiceCustomerGroupServiceId}
                value={serviceCustomerGroupServiceId}
                {...{}}
              >
                {services.map(service => (
                  <option value={service.id} key={service.id}>{service.name}</option>
                ))}
              </select>
            </div>
          </div>
        </li>
      ))}
    </ul>
  </div>
);
SelectAudience.propTypes = {
  audience: PropTypes.objectOf(
    PropTypes.shape({
      type: PropTypes.string,
      customers: PropTypes.number,
    })
  ),
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectAll: PropTypes.func.isRequired,
  selectCustom: PropTypes.func.isRequired,
  toggleAudience: PropTypes.func.isRequired,
  newCustomerWeeks: PropTypes.number.isRequired,
  changeNewCustomerWeeks: PropTypes.func.isRequired,
  services: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  serviceCustomerGroupServiceId: PropTypes.string.isRequired,
  changeServiceCustomerGroupServiceId: PropTypes.func.isRequired,
};
SelectAudience.defaultProps = {
  audience: {},
  services: [],
};

export default SelectAudience;
